import { useEffect } from 'react'

import { useSelectors } from '../store'
import { useServicesApi } from '../store/hooks/use-services-api'

// /api/v2/services, as the marketing pages read it: the catalogue itself, not
// only its prices.
//
// This is what /services uses in place of lib/use-live-price.js (which the
// booking screen and the time slots still use, unchanged). useLivePrice
// answered one question, "what does this name cost", which only makes sense if
// the page already knows which names it sells, and that is exactly the
// assumption that broke: the page's list and the database's list were two
// lists and they disagreed for a day. This hook hands over the list.
//
// `status` is what a page needs to be honest about an empty list:
//
//   'loading'  nothing yet. Render a skeleton of the right size, never an
//              empty section and never a layout that jumps when it lands.
//   'ready'    rows, or genuinely nothing to sell.
//   'failed'   the fetch did not come back. Say so; do not imply we sell
//              nothing.
//
// Entries present always win over a 'failed' status: use-services-api.js
// dispatches the rows before the rest of its own bookkeeping, so a throw after
// that point must not blank a list we already have.
//
// `fetchIfMissing` IS NOT DECORATION. The effect's guard reads the store as it
// was when the component mounted, so two sections of one page that both ask
// for the fetch both fire it: effects in a single commit all run before any of
// them can see the other's dispatch. Exactly one section per page owns the
// request (on /services that is Extras, which cannot render at all without
// it), and the others read what it puts in the store.
export const usePublishedCatalogue = ({ fetchIfMissing = true } = {}) => {
  const { apiServicesFetchAll } = useServicesApi()
  const { prices, servicesStatus } = useSelectors()

  useEffect(() => {
    if (fetchIfMissing && (prices?.ids?.length || 0) === 0) apiServicesFetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const entries = prices?.byOrder || []
  const status = entries.length > 0 ? 'ready' : servicesStatus === 'failed' ? 'failed' : 'loading'

  return { entries, status }
}
